import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

import { Sparkle } from 'lucide-react';

import Badge from '../../../Badge.jsx';
import Button from '../../../Button.jsx';
import ProgressBar from '../../../ProgressBar.jsx';
import { toneFor } from '../constants/statTones';
import { formatRatio, formatValue } from '../shared';

/** Priority → the tone it reads in. Anything else stays neutral. */
const TONE_BY_PRIORITY = Object.freeze({
  HIGH: 'critical',
  MEDIUM: 'warning',
  LOW: 'neutral',
});

/**
 * What the engine recommends next.
 *
 * This widget **proposes**; `AiSummaryWidget` **interprets**. The difference
 * is the button: a recommendation is something you can act on, a read is not.
 *
 * ## Stacked, not paged
 *
 * It used to show one at a time behind arrows, on the reasoning that the
 * engine has already prioritised and handing the reader five is not trusting
 * it. The contract disagrees in a way worth taking seriously: it caps the
 * list at `max_items` — three — and sends all three. They are already the
 * short list, and a reader deciding what to do next compares them. Paging
 * made two of three invisible and the comparison impossible.
 *
 * ## Reading the contract (CT-5641)
 *
 * `actions[]` carries four keys the old `{title, description, timeAgo}` shape
 * had nowhere for:
 *
 * - **`priority`** → the badge, beside the title. It is how the engine ranked
 *   this against the others and the first thing that decides whether to act.
 * - **`score`** → a bar, not a sentence. Stacked, the bars line up and the
 *   ranking is visible without reading three percentages.
 * - **`due_date`** → when the action stops being useful. It replaces
 *   `timeAgo`, which said when the recommendation was raised — the wrong end
 *   of the question for something you are deciding whether to do.
 * - **`available: false`** → the action exists but cannot be taken yet. The
 *   button is disabled rather than hidden, because a recommendation whose
 *   action silently vanishes reads as a bug.
 *
 * `compact` drops the score bar and the due date, not the reasoning. When a
 * slot is narrow the thing to cut is the ranking detail; *why* it is being
 * recommended is the whole basis for acting on it.
 *
 * @param {object} props
 * @param {{ actions: object[] }} props.data `NEXT_BEST_ACTION`'s `actions[]`, or the pre-v2 `items[]`.
 * @param {(action: object) => void} [props.onViewDetails] Omit to render without the action.
 * @param {string} [props.actionLabel='View details']
 * @param {boolean} [props.compact=false]
 * @param {string} [props.className]
 */
function NextBestActionWidget({
  data,
  onViewDetails,
  actionLabel = 'View details',
  compact = false,
  className = '',
}) {
  const items = useMemo(() => {
    if (data?.actions) {
      return data.actions.map((action, index) => ({
        key: action.row_key ?? action.action_code ?? String(index),
        title: action.title,
        description: action.reason ?? '',
        priority: action.priority ?? null,
        score: typeof action.score === 'number' ? action.score : null,
        due: action.due_date ? formatValue(action.due_date, { style: 'DATE' }) : '',
        available: action.available !== false,
        raw: action,
      }));
    }
    return (data?.items ?? []).map((item, index) => ({
      key: item.id ?? String(index),
      title: item.title,
      description: item.description ?? '',
      priority: null,
      score: null,
      due: item.timeAgo ?? '',
      available: true,
      raw: item,
    }));
  }, [data]);

  if (!items.length) return null;

  return (
    <div className={`flex min-w-0 flex-col gap-2 ${className}`.trim()}>
      <div className="flex min-w-0 items-center gap-1.5">
        <Sparkle size={13} className="shrink-0 text-purple-500 dark:text-purple-300" aria-hidden />
        <span className="text-[11px] font-medium uppercase tracking-wide text-content-secondary">
          Next best action
        </span>
      </div>

      {items.map((item) => (
        <div
          key={item.key}
          className="flex min-w-0 flex-col gap-1.5 rounded-lg border border-edge bg-surface-muted p-3"
        >
          <div className="flex min-w-0 items-start justify-between gap-2">
            <span className="min-w-0 text-[13px] font-semibold leading-snug text-content">
              {item.title}
            </span>
            {item.priority ? (
              <span className="shrink-0">
                <Badge size="small" variant={toneFor(TONE_BY_PRIORITY[item.priority]).badge}>
                  {item.priority}
                </Badge>
              </span>
            ) : null}
          </div>

          {item.description ? (
            <p className="m-0 text-[11px] leading-relaxed text-content-muted">
              {item.description}
            </p>
          ) : null}

          {!compact && (item.score !== null || item.due) ? (
            <div className="flex min-w-0 items-center gap-2">
              {item.score !== null ? (
                <>
                  <ProgressBar
                    className="min-w-0 flex-1"
                    value={Math.round(item.score * 100)}
                    tone="purple"
                    showLabel={false}
                    ariaLabel={`Score ${formatRatio(item.score, { signed: false, decimals: 0 })}`}
                  />
                  <span className="shrink-0 text-[11px] font-semibold text-content">
                    {item.score.toFixed(2)}
                  </span>
                </>
              ) : null}
              {item.due ? (
                <span className="shrink-0 text-[10px] text-content-faint">{`due ${item.due}`}</span>
              ) : null}
            </div>
          ) : null}

          {onViewDetails ? (
            <div>
              <Button
                variant="tertiary"
                size="sm"
                disabled={!item.available}
                onClick={() => onViewDetails(item.raw)}
              >
                {actionLabel}
              </Button>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

NextBestActionWidget.propTypes = {
  /** `NEXT_BEST_ACTION`'s `actions[]`. The pre-v2 `items[]` still renders. */
  data: PropTypes.shape({
    actions: PropTypes.arrayOf(
      PropTypes.shape({
        row_key: PropTypes.string,
        action_code: PropTypes.string,
        title: PropTypes.string.isRequired,
        reason: PropTypes.string,
        score: PropTypes.number,
        priority: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        due_date: PropTypes.string,
        available: PropTypes.bool,
      }),
    ),
    context_refs: PropTypes.arrayOf(PropTypes.object),
    /** The pre-v2 shape, still accepted. */
    items: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onViewDetails: PropTypes.func,
  actionLabel: PropTypes.string,
  compact: PropTypes.bool,
  className: PropTypes.string,
};

export default memo(NextBestActionWidget);
